import React, { useEffect, useRef, useState } from 'react';
import './AgencyMap.css';
import { getAgencies } from '../../services/agenciesService';

const AgencyMap = () => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);

  // Map API response (snake_case) to component format (camelCase)
  const mapAgencyFromAPI = (apiAgency) => {
    return {
      id: apiAgency.id?.toString() || apiAgency.agency_id || '',
      agencyName: apiAgency.agency_name || '',
      city: apiAgency.city || '',
      state: apiAgency.state || '',
      latitude: apiAgency.latitude != null ? parseFloat(apiAgency.latitude) : null,
      longitude: apiAgency.longitude != null ? parseFloat(apiAgency.longitude) : null,
      status: apiAgency.status || 'Active'
    };
  };

  // Fetch agencies from API
  useEffect(() => {
    const fetchAgencies = async () => {
      setLoading(true);
      try {
        // Fetch all agencies (API max limit is 100)
        const response = await getAgencies({ page: 1, limit: 100 });
        
        // Normalize response to support multiple API shapes
        const extractItems = (resp) => {
          if (!resp) return [];
          const candidate = resp.data !== undefined ? resp : { data: resp };
          if (Array.isArray(candidate.data)) return candidate.data;
          if (candidate.data && Array.isArray(candidate.data.items)) return candidate.data.items;
          if (Array.isArray(resp)) return resp;
          if (Array.isArray(resp.items)) return resp.items;
          return candidate.data?.items || resp?.items || [];
        };

        const agenciesData = extractItems(response);
        const mappedAgencies = (Array.isArray(agenciesData) ? agenciesData : []).map(mapAgencyFromAPI);
        
        // Filter agencies that have valid latitude and longitude
        const validAgencies = mappedAgencies.filter(agency => 
          agency.latitude != null && 
          agency.longitude != null && 
          !isNaN(agency.latitude) && 
          !isNaN(agency.longitude) &&
          agency.latitude >= -90 && agency.latitude <= 90 &&
          agency.longitude >= -180 && agency.longitude <= 180
        );
        
        setAgencies(validAgencies);
      } catch (error) {
        console.error('Error fetching agencies for map:', error);
        setAgencies([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAgencies();
  }, []);

  useEffect(() => {
    if (!mapRef.current || loading) return;

    // Initialize map centered on India
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = window.L.map(mapRef.current).setView([20.5937, 78.9629], 5);

      // Add OpenStreetMap tiles
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(mapInstanceRef.current);
    }

    const map = mapInstanceRef.current;

    // Clear existing markers
    markersRef.current.forEach(marker => {
      map.removeLayer(marker);
    });
    markersRef.current = [];

    // Add markers for each agency
    agencies.forEach(agency => {
      // Create custom icon based on status
      const iconColor = agency.status === 'Active' ? '#10b981' : '#ef4444';
      const iconHtml = `
        <div style="
          background-color: ${iconColor};
          width: 30px;
          height: 30px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          border: 3px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <div style="
            transform: rotate(45deg);
            color: white;
            font-size: 16px;
            font-weight: bold;
          ">🏢</div>
        </div>
      `;

      const customIcon = window.L.divIcon({
        html: iconHtml,
        className: 'custom-marker',
        iconSize: [30, 30],
        iconAnchor: [15, 30],
        popupAnchor: [0, -30]
      });

      // Create marker
      const marker = window.L.marker([agency.latitude, agency.longitude], { icon: customIcon })
        .addTo(map)
        .bindPopup(`
          <div style="padding: 8px; min-width: 200px;">
            <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #111827;">
              ${agency.agencyName}
            </h3>
            <p style="margin: 4px 0; font-size: 14px; color: #6b7280;">
              <strong>ID:</strong> ${agency.id}
            </p>
            <p style="margin: 4px 0; font-size: 14px; color: #6b7280;">
              <strong>City:</strong> ${agency.city}${agency.state ? `, ${agency.state}` : ''}
            </p>
            <p style="margin: 4px 0; font-size: 14px; color: #6b7280;">
              <strong>Status:</strong> 
              <span style="
                display: inline-block;
                padding: 2px 8px;
                border-radius: 12px;
                font-size: 12px;
                font-weight: 500;
                margin-left: 4px;
                background-color: ${agency.status === 'Active' ? '#d1fae5' : '#fee2e2'};
                color: ${agency.status === 'Active' ? '#065f46' : '#991b1b'};
              ">
                ${agency.status}
              </span>
            </p>
          </div>
        `);

      markersRef.current.push(marker);
    });

    // Fit map to show all markers
    if (markersRef.current.length > 0) {
      const group = new window.L.featureGroup(markersRef.current);
      map.fitBounds(group.getBounds().pad(0.1));
    } else {
      // If no markers, keep default view centered on India
      map.setView([20.5937, 78.9629], 5);
    }

    return () => {
      // Cleanup is handled by React
    };
  }, [agencies, loading]);

  return (
    <div className="agency-map-container">
      <div className="agency-map-header">
        <h2 className="agency-map-title">Transporters Map</h2>
        <div className="agency-map-legend">
          <div className="legend-item">
            <div className="legend-icon active"></div>
            <span>Active</span>
          </div>
          <div className="legend-item">
            <div className="legend-icon inactive"></div>
            <span>Inactive</span>
          </div>
        </div>
      </div>
      {loading ? (
        <div className="agency-map" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
          <div>Loading transporters...</div>
        </div>
      ) : agencies.length === 0 ? (
        <div className="agency-map" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
          <div>No transporters with location data available</div>
        </div>
      ) : (
        <div ref={mapRef} className="agency-map" id="agency-map"></div>
      )}
    </div>
  );
};

export default AgencyMap;

